<template>
  <LoadingComponent
    v-if="!localDashboard || localDashboard.id !== currentBoard.id"
  />
  <template v-if="localDashboard">
    <div class="flex flex-shrink-0 flex-col gap-2">
      <QuickPeriodBar
        ref="quickPeriodBar"
        v-model="selectedPeriod"
        :periods="periodPresets"
        :date-start="activeDateStart"
        :date-end="activeDateEnd"
        @update:model-value="onPeriodPresetChange"
      >
        <template #actions>
          <DashboardPanelControl
            ref="dashboardControl"
            v-model:dashboard="localDashboard"
            v-model:need-save-locked="loading"
            v-model:require-save-dashboard="requireSaveDashboard"
            :count-filter="activeFiltersCount"
            :is-dirty="hasCustomizedFilters"
            @update:dashboard="getAnalyticsData"
            @clear-filters="clearFilters"
          >
            <template #filter-content="{ close }">
              <AnalyticsTimeFilterPanel
                :granularity="granularity"
                :based-on="basedOn"
                :date-start="activeDateStart"
                :date-end="activeDateEnd"
                :highlight-date-range-trigger="highlightDateRangeTrigger"
                :funeral-homes="contextFuneralHomes"
                :companies="contextCompanies"
                :compare-mode="contextCompareMode"
                :is-invoicing-dashboard="isInvoicingDashboard"
                :doc-types="activeDocTypeFilter"
                :responsables="activeResponsableFilter"
                :excluded-dossier-statuses="activeExcludedDossierStatuses"
                @apply="(params) => applyTimeFilters(params, close)"
              />
            </template>
          </DashboardPanelControl>
        </template>
      </QuickPeriodBar>
    </div>

    <div
      v-if="localDashboard"
      class="relative flex min-h-0 flex-1 flex-col overflow-y-auto"
    >
      <div
        v-if="loading"
        class="absolute inset-0 z-10 flex items-center justify-center"
      >
        <LoadingComponent />
      </div>
      <div class="flex flex-col gap-3">
        <AnalyticsArticleStats
          v-if="isInvoiceItemDashboard && invoiceItemStatistics"
          :statistics="invoiceItemStatistics"
          :config="{}"
          :start-date="activeDateStart"
          :end-date="activeDateEnd"
          kpi-only
        />
        <AnalyticsInvoicingStats
          v-if="isInvoicingDashboard && invoicingStatistics"
          :statistics="invoicingStatistics"
          kpi-only
        />
        <div
          v-for="(row, idx) in localDashboard.rows"
          :key="row.id"
          class="flex flex-col gap-1"
        >
          <div v-if="row.label">
            <h3 class="text-xl font-bold capitalize">{{ $t(row.label) }}</h3>
          </div>
          <div
            class="lg:gap- grid grid-cols-1 gap-4 md:grid-cols-3 lg:grid-cols-4"
          >
            <template v-for="(element, idy) in row.elements" :key="element.id">
              <SimpleTile
                v-if="row.type === 'simple'"
                v-model:element="localDashboard.rows[idx].elements[idy]"
                :data="analyticsData?.[element.id]?.data ?? null"
                :data-type="analyticsData?.[element.id]?.data_type ?? 'scalar'"
                :dashboard-id="localDashboard.id"
              />
              <ChartTile
                v-else
                v-model:element="localDashboard.rows[idx].elements[idy]"
                :data="analyticsData?.[element.id] ?? null"
                :uniqid="`${idx}-${idy}`"
                :dashboard-id="localDashboard.id"
              />
            </template>
          </div>
        </div>
      </div>
    </div>
  </template>
</template>

<script>
import { mapActions, mapGetters } from 'vuex'
import LoadingComponent from '@c/TheLoadingComponent.vue'
import DashboardPanelControl from '@c/Dashboard/Components/DashboardPanelControl.vue'
import SimpleTile from '@c/Dashboard/Content/Analytics/component/SimpleTile.vue'
import ChartTile from '@c/Dashboard/Content/Analytics/component/ChartTile.vue'
import QuickPeriodBar from '@c/QuickPeriodBar.vue'
import AnalyticsTimeFilterPanel from '@c/Dashboard/Content/Analytics/component/AnalyticsTimeFilterPanel.vue'

import tempAnalytics from '@/assets/settings/tempAnalytics.js'
import AnalyticsArticleStats from '@/views/analytics/component/AnalyticsArticleStats.vue'
import AnalyticsInvoicingStats from '@/views/analytics/component/AnalyticsInvoicingStats.vue'
import {
  getStandardPeriodPresets,
  getDateRangeForPeriod
} from '@u/helpers/periodPresets.js'
import persistViewFiltersStateMixin from '@/mixins/persistViewFiltersStateMixin.js'
import {
  DOSSIER_STATUS_FILTER_COLUMN,
  RESPONSABLE_FILTER_COLUMN,
  buildInitialFilters,
  normalizeResponsableFilters,
  withDefaultDossierStatusFilter
} from '@u/helpers/analyticsFilters'
import moment from 'moment'

// Maps a dashboard group to the row IDs it should display from tempAnalytics
const GROUP_ROW_MAP = {
  dossier: ['R1', 'R5'],
  invoicing: ['R2'],
  event: ['R3', 'R3P'],
  invoice_item: ['R6']
}

function buildTempConfig(t, group) {
  const config = tempAnalytics(t)
  if (group && GROUP_ROW_MAP[group]) {
    config.rows = config.rows.filter((row) =>
      GROUP_ROW_MAP[group].includes(row.id)
    )
  }
  return config
}

const DATE_FILTER_COLUMNS = [
  'common#date_range',
  'common#date_relative',
  'common#date_specific'
]
const CONTEXT_FILTER_COLUMNS = ['common#funeral_home', 'common#company']

export default {
  name: 'AnalyticsContent',
  components: {
    QuickPeriodBar,
    AnalyticsTimeFilterPanel,
    SimpleTile,
    ChartTile,
    DashboardPanelControl,
    LoadingComponent,
    AnalyticsArticleStats,
    AnalyticsInvoicingStats
  },
  mixins: [persistViewFiltersStateMixin],
  persistViewState: [
    'selectedPeriod',
    'granularity',
    'basedOn',
    'customDateStart',
    'customDateEnd',
    'contextFuneralHomes',
    'contextCompanies',
    'contextCompareMode',
    'localDashboard.filters'
  ],
  props: {
    currentBoard: {
      type: Object,
      required: true
    }
  },
  data() {
    return {
      loading: false,
      localDashboard: null,
      need_save_locked: true,
      requireSaveDashboard: false,
      analyticsData: null,
      invoiceItemStatistics: null,
      invoicingStatistics: null,
      selectedPeriod: 'this_month',
      granularity: 'month',
      basedOn: 'element_date',
      customDateStart: null,
      customDateEnd: null,
      // Advanced filters are per-view state (persisted by the mixin, BBSW-8566):
      // the Vuex activeContextFilters only mirror the active view so detail
      // pages can inherit them — views never read them back from the store.
      contextFuneralHomes: [],
      contextCompanies: [],
      contextCompareMode: 'full',
      highlightDateRangeTrigger: 0
    }
  },
  computed: {
    ...mapGetters({
      availableFilter: 'metadata/getAvailableFilterAnalytics'
    }),
    periodPresets() {
      return getStandardPeriodPresets().map((preset) => ({
        value: preset.value,
        label: this.$t(preset.labelKey)
      }))
    },
    activeDateStart() {
      if (this.selectedPeriod === 'custom') {
        return this.customDateStart
      }
      return getDateRangeForPeriod(this.selectedPeriod)?.start ?? null
    },
    activeDateEnd() {
      if (this.selectedPeriod === 'custom') {
        return this.customDateEnd
      }
      return getDateRangeForPeriod(this.selectedPeriod)?.end ?? null
    },
    isInvoiceItemDashboard() {
      return this.localDashboard?.group === 'invoice_item'
    },
    isInvoicingDashboard() {
      return this.localDashboard?.group === 'invoicing'
    },
    activeFiltersCount() {
      const funeralHomes = this.contextFuneralHomes
      const companies = this.contextCompanies

      const customFilters = (this.localDashboard?.filters ?? []).filter(
        (f) =>
          !DATE_FILTER_COLUMNS.includes(f.column) &&
          !CONTEXT_FILTER_COLUMNS.includes(f.column) &&
          f.values?.length > 0
      ).length

      return (
        customFilters +
        [funeralHomes, companies].filter((v) => v.length > 0).length
      )
    },
    /**
     * Returns the currently selected doc type values from localDashboard.filters,
     * or an empty array when no filter is set (backend default INV will apply).
     * @returns {string[]}
     */
    activeDocTypeFilter() {
      if (!this.localDashboard?.filters) {
        return []
      }
      const entry = this.localDashboard.filters.find(
        (f) => f.column === 'invoicing#doc_type'
      )
      return Array.isArray(entry?.values) ? entry.values : []
    },
    /**
     * Returns the currently selected responsable user ids from localDashboard.filters,
     * or an empty array when no filter is set.
     * @returns {string[]}
     */
    activeResponsableFilter() {
      if (!this.localDashboard?.filters) {
        return []
      }
      const entry = this.localDashboard.filters.find(
        (f) => f.column === RESPONSABLE_FILTER_COLUMN
      )
      return Array.isArray(entry?.values) ? entry.values : []
    },
    /**
     * Dossier statuses excluded from the statistics (BBSW-10888), [] when none.
     * @returns {string[]}
     */
    activeExcludedDossierStatuses() {
      const entry = (this.localDashboard?.filters ?? []).find(
        (f) => f.column === DOSSIER_STATUS_FILTER_COLUMN
      )
      return entry?.condition === 'not_in' && Array.isArray(entry.values)
        ? entry.values
        : []
    },
    /**
     * Whether the panel differs from the dashboard defaults: drives the reset button.
     * Funeral homes and companies are view context, outside localDashboard.filters.
     * @returns {boolean}
     */
    hasCustomizedFilters() {
      return (
        this.$isStateDirty('localDashboard.filters') ||
        this.contextFuneralHomes.length > 0 ||
        this.contextCompanies.length > 0
      )
    }
  },
  watch: {
    currentBoard(newBoard, oldBoard) {
      if (newBoard?.id === oldBoard?.id) return
      this.fetchAnalyticsDashboard()
    }
  },
  mounted() {
    // Prevent broken session restore: 'custom' was saved but customDateStart=null
    // (null equals default so the mixin skips it). Fall back to the default preset.
    if (this.selectedPeriod === 'custom' && !this.customDateStart) {
      this.selectedPeriod = 'this_month'
    }
    this.fetchAnalyticsDashboard()
  },
  methods: {
    ...mapActions({
      processFetchTemplateDashboardByIdAction:
        'dashboard/fetchTemplateDashboardById',
      processUpdateTemplateDashboardByIdAction:
        'dashboard/updateTemplateDashboardById',
      processGetAnalyticsForDashboardDataAction:
        'analytics/getAnalyticsForDashboardData',
      processGetInvoiceItemStatistics: 'analytics/getInvoiceItemStatistics',
      processGetInvoicingStatistics: 'analytics/getInvoicingStatistics'
    }),
    /**
     * Builds the non-date filter array from localDashboard.filters, stripping date and
     * context columns, stripping entries with empty values, and re-injecting the active
     * funeralHomes/companies context values.
     * @returns {Array} filter entries ready to be sent to the analytics API
     */
    buildNonDateFilters() {
      const funeralHomes = this.contextFuneralHomes
      const companies = this.contextCompanies
      const filters = (this.localDashboard.filters ?? []).filter(
        (f) =>
          !DATE_FILTER_COLUMNS.includes(f.column) &&
          !CONTEXT_FILTER_COLUMNS.includes(f.column) &&
          (f.values == null ||
            (Array.isArray(f.values) ? f.values.length > 0 : true))
      )
      if (funeralHomes.length > 0) {
        filters.push({
          column: 'common#funeral_home',
          values: funeralHomes,
          condition: 'in',
          whereType: 'where'
        })
      }
      if (companies.length > 0) {
        filters.push({
          column: 'common#company',
          values: companies,
          condition: 'in',
          whereType: 'where'
        })
      }
      return filters
    },

    /**
     * Computes compare_start / compare_end for the N-1 comparison period.
     * - 'full' mode  : same calendar period one year ago (full range)
     * - 'today' mode : capped at min(today, endDate) one year ago
     * @returns {{ compareStart: string|null, compareEnd: string|null }}
     */
    resolveCompareDates() {
      if (!this.activeDateStart || !this.activeDateEnd) {
        return { compareStart: null, compareEnd: null }
      }
      const compareMode = this.contextCompareMode
      return {
        compareStart: moment(this.activeDateStart)
          .subtract(1, 'year')
          .format('YYYY-MM-DD'),
        compareEnd:
          compareMode === 'today'
            ? moment
                .min(moment(), moment(this.activeDateEnd))
                .subtract(1, 'year')
                .format('YYYY-MM-DD')
            : moment(this.activeDateEnd)
                .subtract(1, 'year')
                .format('YYYY-MM-DD')
      }
    },

    /**
     * Builds the full API payload for getAnalyticsForDashboardData.
     *
     * Per-element date filters in rows are also replaced with the active date range:
     * chart elements (donut/pie) carry hardcoded 'this_year' filters in tempAnalytics.js
     * that the backend uses directly, bypassing the global payload filters. Replacing
     * date columns per-element ensures all elements respect the period selected in the
     * QuickPeriodBar.
     *
     * @returns {object}
     */
    buildApiPayload() {
      const tempConfig = buildTempConfig(
        this.$t.bind(this),
        this.localDashboard.group
      )
      const dateFilter =
        this.activeDateStart && this.activeDateEnd
          ? {
              whereType: 'where',
              column: 'common#date_range',
              values: { start: this.activeDateStart, end: this.activeDateEnd },
              condition: '='
            }
          : null
      const nonDateFilters = this.buildNonDateFilters()
      // The backend takes the common# filters from the payload and the group filters
      // (dossier#, event#, invoicing#) from each ELEMENT, so the panel filters must also
      // be pushed into every element — replacing the element's own entry on column
      // collision, same pattern as the date — for the group-namespaced ones (doc type)
      // and for the detail page, which only reads the element filters.
      const nonDateColumns = nonDateFilters.map((filter) => filter.column)
      const ytd = this.contextCompareMode === 'today'
      const updatedRows = (this.localDashboard.rows ?? []).map((row) => ({
        ...row,
        elements: (row.elements ?? []).map((element) => ({
          ...element,
          filters: [
            ...(element.filters ?? []).filter(
              (f) =>
                !DATE_FILTER_COLUMNS.includes(f.column) &&
                !nonDateColumns.includes(f.column)
            ),
            ...nonDateFilters,
            ...(dateFilter ? [dateFilter] : [])
          ],
          options: {
            ...element.options,
            x_axis: {
              ...element.options?.x_axis,
              ytd
            }
          }
        }))
      }))

      return {
        ...this.localDashboard,
        ...tempConfig,
        rows: updatedRows,
        display_options: {
          ...this.localDashboard.display_options,
          ...tempConfig.display_options,
          based_on: this.basedOn,
          forced_granularity: this.granularity
        },
        filters: dateFilter ? [...nonDateFilters, dateFilter] : nonDateFilters
      }
    },
    /**
     * Triggers all analytics requests for the current dashboard.
     * Captures the dashboard ID at call time to discard stale responses if the user
     * navigates away before the request resolves.
     */
    getAnalyticsData() {
      this.syncFiltersToStore()
      const dashboardId = this.localDashboard.id
      this.loading = true

      const requests = [
        this.processGetAnalyticsForDashboardDataAction(this.buildApiPayload())
      ]

      if (this.isInvoiceItemDashboard) {
        requests.push(this.fetchInvoiceItemStatistics())
      }
      if (this.isInvoicingDashboard) {
        requests.push(this.fetchInvoicingStatistics())
      }

      Promise.all(requests)
        .then(([analyticsResponse]) => {
          if (this.localDashboard?.id !== dashboardId) return
          this.analyticsData = analyticsResponse.analytics_data
        })
        .catch(() => {
          // silently ignore — loading is cleared in finally
        })
        .finally(() => {
          if (this.localDashboard?.id === dashboardId) {
            this.loading = false
          }
        })
    },
    /**
     * Fetches invoice item statistics (types vendus, top articles) for the Articles dashboard.
     * @returns {Promise}
     */
    fetchInvoiceItemStatistics() {
      const { compareStart, compareEnd } = this.resolveCompareDates()
      return this.processGetInvoiceItemStatistics({
        config: { filters: this.buildNonDateFilters() },
        start_date: this.activeDateStart,
        end_date: this.activeDateEnd,
        compare_start: compareStart,
        compare_end: compareEnd
      }).then((response) => {
        this.invoiceItemStatistics = response.statistics ?? null
      })
    },
    /**
     * Fetches invoicing statistics (CA TTC/HTVA, statut, catégories) for the Facturation dashboard.
     * @returns {Promise}
     */
    fetchInvoicingStatistics() {
      const { compareStart, compareEnd } = this.resolveCompareDates()
      return this.processGetInvoicingStatistics({
        config: { filters: this.buildNonDateFilters() },
        start_date: this.activeDateStart,
        end_date: this.activeDateEnd,
        compare_start: compareStart,
        compare_end: compareEnd
      }).then((response) => {
        this.invoicingStatistics = response.statistics ?? null
      })
    },
    /**
     * Handles a period preset button click from QuickPeriodBar.
     *
     * For 'custom': preserves current active dates so the picker has values when it
     * renders, then opens the filter panel. setTimeout(0) is required to avoid a race
     * with Headless UI's outside-click handler, which would close the popover immediately
     * if opened inside the same microtask as the button click.
     *
     * querySelector on QuickPeriodBar.$el is used instead of $refs.dashboardControl.$el
     * because DashboardPanelControl renders as a Vue fragment (unreliable $el), while
     * QuickPeriodBar has a single <div> root. Headless UI PopoverButton exposes $el as a
     * Ref<HTMLElement>, so direct .click() via querySelector is more reliable.
     *
     * @param {string} period - preset key (e.g. 'this_month', 'last_year', 'custom')
     */
    onPeriodPresetChange(period) {
      if (period === 'custom') {
        this.customDateStart = this.activeDateStart
        this.customDateEnd = this.activeDateEnd
      }
      this.selectedPeriod = period
      if (period === 'custom') {
        setTimeout(() => {
          const bar = this.$refs.quickPeriodBar?.$el
          const filterBtn =
            bar instanceof HTMLElement
              ? bar.querySelector('[aria-expanded]')
              : null
          if (filterBtn instanceof HTMLElement) {
            filterBtn.click()
          } else {
            this.$refs.dashboardControl?.openFilters()
          }
          this.$nextTick(() => {
            this.highlightDateRangeTrigger++
          })
        }, 0)
      } else {
        this.syncFiltersToStore()
        this.getAnalyticsData()
      }
    },
    /**
     * Writes the current date/granularity/period/context state to the Vuex analytics store
     * so detail pages can inherit the same filters without relying on URL query params.
     */
    syncFiltersToStore() {
      this.$store.commit('analytics/setActiveContextFilters', {
        funeralHomes: this.contextFuneralHomes,
        companies: this.contextCompanies,
        responsables: this.activeResponsableFilter,
        excludedDossierStatuses: this.activeExcludedDossierStatuses,
        compareMode: this.contextCompareMode,
        dateStart: this.activeDateStart,
        dateEnd: this.activeDateEnd,
        basedOn: this.basedOn,
        granularity: this.granularity,
        selectedPeriod: this.selectedPeriod
      })
    },
    /**
     * Applies the full filter panel state (time + context + compare mode + doc types) to the dashboard.
     * Only switches selectedPeriod to 'custom' if the dates actually changed, preserving
     * the active preset label (e.g. 'last_year') when only granularity or compare mode is updated.
     *
     * Doc type selection is written into localDashboard.filters (column invoicing#doc_type).
     * When docTypes is empty the entry is removed so the backend default (INV) applies.
     * The responsable selection is a common filter, applied to every element of the
     * dashboard (BBSW-9775). The dossier status selection is stored as an exclusion
     * list (BBSW-10888); on a predefined page an empty exclusion is kept as an explicit
     * "include everything" choice so the prospect/cancelled default is not re-applied.
     *
     * @param {{ granularity: string, basedOn: string, dateStart: string, dateEnd: string, funeralHomes: string[], companies: string[], compareMode: string, responsables: string[], excludedDossierStatuses: string[], docTypes: string[] }} params
     * @param {Function|undefined} close - optional popover close callback from DashboardPanelControl
     */
    applyTimeFilters(
      {
        granularity,
        basedOn,
        dateStart,
        dateEnd,
        funeralHomes,
        companies,
        compareMode,
        responsables,
        excludedDossierStatuses,
        docTypes
      },
      close
    ) {
      this.granularity = granularity
      this.basedOn = basedOn

      if (
        this.selectedPeriod === 'custom' ||
        dateStart !== this.activeDateStart ||
        dateEnd !== this.activeDateEnd
      ) {
        this.customDateStart = dateStart
        this.customDateEnd = dateEnd
        if (dateStart && dateEnd) {
          this.selectedPeriod = 'custom'
        }
      }

      if (this.isInvoicingDashboard) {
        this.setPanelFilter(
          'invoicing#doc_type',
          docTypes?.length > 0 ? docTypes : null,
          'in'
        )
      }

      this.setPanelFilter(
        RESPONSABLE_FILTER_COLUMN,
        responsables?.length > 0 ? responsables : null,
        'in'
      )

      const keepsEmptyStatusExclusion = this.localDashboard?.is_default
      this.setPanelFilter(
        DOSSIER_STATUS_FILTER_COLUMN,
        excludedDossierStatuses?.length > 0 || keepsEmptyStatusExclusion
          ? (excludedDossierStatuses ?? [])
          : null,
        'not_in'
      )

      this.contextFuneralHomes = funeralHomes
      this.contextCompanies = companies
      this.contextCompareMode = compareMode

      if (typeof close === 'function') close()

      // getAnalyticsData() starts with syncFiltersToStore(), which mirrors the
      // per-view context filters to the store for detail pages.
      this.getAnalyticsData()
    },
    /**
     * Replaces the panel filter of a column in place (keeping the entry order, so an
     * unchanged selection does not look modified), or removes it when values is null.
     * @param {string} column
     * @param {Array|null} values
     * @param {string} condition
     */
    setPanelFilter(column, values, condition) {
      if (!this.localDashboard) {
        return
      }
      const filters = [...(this.localDashboard.filters ?? [])]
      const index = filters.findIndex((f) => f.column === column)
      const entry =
        values === null
          ? null
          : { column, values, condition, whereType: 'where' }

      if (index === -1 && entry) {
        filters.push(entry)
      } else if (index !== -1 && entry) {
        filters.splice(index, 1, entry)
      } else if (index !== -1) {
        filters.splice(index, 1)
      }

      this.localDashboard.filters = filters
    },
    /**
     * Resets the panel to the dashboard defaults (predefined pages: prospects and
     * cancelled dossiers excluded) and clears the funeral home/company context.
     */
    clearFilters() {
      this.localDashboard.filters = this.$getInitialState(
        'localDashboard.filters'
      )
      this.contextFuneralHomes = []
      this.contextCompanies = []
      this.getAnalyticsData()
    },
    /**
     * Persists dashboard config changes (e.g. column order, element visibility) to the server.
     */
    handleSaveDashboard() {
      this.processUpdateTemplateDashboardByIdAction({
        id: this.localDashboard.id,
        data: this.localDashboard
      })
        .then((response) => {
          this.localDashboard.analytic_rows =
            response?.data?.template_dashboard.analytic_rows
          this.localDashboardOrdered(this.localDashboard)
        })
        .catch(() => {
          this.$h.toastDanger(this.$t('toasts.something_went_wrong'))
        })
    },
    /**
     * Fetches the dashboard config from the server and triggers a full data load.
     * Captures the target ID at call time to discard stale responses when the user
     * navigates quickly between dashboards.
     *
     * After the response populates localDashboard, the per-view saved filters are
     * re-applied (same pattern as InvoicingContent.fetchTemplateDashboardConfig):
     * the persistViewFiltersStateMixin restores state at mount, but this fetch
     * overwrites localDashboard entirely, which would otherwise lose the restored
     * filters. Saved session state wins over the fetched default.
     */
    fetchAnalyticsDashboard() {
      const targetId = this.currentBoard.id
      this.invoiceItemStatistics = null
      this.invoicingStatistics = null
      this.processFetchTemplateDashboardByIdAction({ id: targetId })
        .then((response) => {
          if (this.currentBoard.id !== targetId) return
          const tempConfig = buildTempConfig(
            this.$t.bind(this),
            response.template_dashboard.group
          )
          const initialFilters = normalizeResponsableFilters(
            buildInitialFilters(response.template_dashboard)
          )
          this.localDashboard = {
            ...response.template_dashboard,
            ...tempConfig,
            filters: initialFilters
          }
          this.$registerInitialState('localDashboard.filters', initialFilters)
          this.restorePersistedFilters(targetId)
          this.getAnalyticsData()
        })
        .catch(() => {
          this.$router.push({ name: 'dashboard_main_page_route' })
        })
    },
    /**
     * Re-applies the per-view saved filters onto the freshly fetched localDashboard.
     * Only localDashboard.filters is re-applied — the other persisted keys (period,
     * granularity, dates) are flat data properties untouched by the fetch.
     *
     * Guards:
     * - no saved state for this view → fetched filters apply untouched
     * - route id differs from the fetched dashboard id (container fallback case
     *   where currentBoard is not the dashboard in the URL) → ignore saved state
     *
     * @param {string} targetId - the dashboard ID that was just fetched
     */
    restorePersistedFilters(targetId) {
      if (this.$route.params?.id !== targetId) {
        return
      }
      const saved = this.$store.getters['viewFiltersState/getState'](
        this._persistedStateKey
      )
      const savedFilters = saved?.['localDashboard.filters']
      if (Array.isArray(savedFilters)) {
        // States saved before BBSW-9775/BBSW-10888 carry a group-namespaced responsable
        // column and no dossier status entry.
        this.localDashboard.filters = withDefaultDossierStatusFilter(
          normalizeResponsableFilters(savedFilters),
          this.localDashboard
        )
      }
    },
    /**
     * Sorts stacked widget datasets by value (descending) so the largest segments
     * appear first in charts and legend tables.
     * @param {object} dashboard
     * @returns {object} dashboard with sorted analytic_rows
     */
    localDashboardOrdered(dashboard) {
      const template_dashboard = dashboard
      const elements = template_dashboard?.analytic_rows[1]?.elements

      if (elements?.length) {
        elements.forEach((element) => {
          const datasets = element?.data?.datasets

          if (datasets) {
            const data = datasets[0]?.data
            const combinedArray = element?.data?.labels
              ?.map((label, index) => ({
                label,
                value: data[0][Object.keys(data[0])[index]]
              }))
              .filter((item) => item.value)
              .sort((a, b) => b.value - a.value)

            datasets[0].data = combinedArray.map((item) => item.value)
            element.data.labels = combinedArray.map((item) => item.label)
          }
        })
      }

      return template_dashboard
    },
    /**
     * Called when the parent signals a dashboard config change (e.g. column added/removed).
     * @param {string} _dashboard_template_id
     */
    dashboardChanged(_dashboard_template_id) {
      this.fetchAnalyticsDashboard()
    }
  }
}
</script>
